/**
 * 画布素材收藏夹（localStorage 轻量实现）
 *
 * 功能：
 *  - 保存图片/视频/文本到收藏夹
 *  - 从收藏夹读取列表
 *  - 删除收藏项
 *
 * 数据结构：
 *  { id, type: 'image'|'video'|'text', content, prompt, createdAt }
 */
export interface SavedAsset {
  id: string;
  type: 'image' | 'video' | 'text';
  content: string; // 图片/视频 URL 或文本内容
  prompt?: string;
  createdAt: number;
  /** 收藏时所在的画布项目：本项目素材 tab 按它过滤；存量数据无此字段归入全局 */
  projectId?: string | null;
  /** 视频素材封面 URL（type='video' 时可选） */
  coverUrl?: string | null;
  /** 视频素材时长（秒，type='video' 时可选，作为下游参考视频计费用） */
  durationSec?: number | null;
}

const STORAGE_KEY = 'canvas_saved_assets';

function loadAssets(): SavedAsset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAssets(assets: SavedAsset[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(assets));
}

export const assetStorage = {
  getAll(): SavedAsset[] {
    return loadAssets();
  },

  add(asset: Omit<SavedAsset, 'id' | 'createdAt'>): { asset: SavedAsset; created: boolean } {
    const items = loadAssets();
    // 去重：相同类型 + 相同内容（URL / 文本）的素材不重复收藏
    const existing = items.find((a) => a.type === asset.type && a.content === asset.content);
    if (existing) return { asset: existing, created: false };

    const newItem: SavedAsset = {
      ...asset,
      id: `asset_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
    };
    items.unshift(newItem);
    // 最多保存 200 条
    if (items.length > 200) items.length = 200;
    saveAssets(items);
    return { asset: newItem, created: true };
  },

  remove(id: string) {
    const items = loadAssets().filter((a) => a.id !== id);
    saveAssets(items);
  },

  clear() {
    localStorage.removeItem(STORAGE_KEY);
  },

  /** 当前项目的收藏（收藏时记录的 projectId 与之匹配） */
  getByProject(projectId: string | null): SavedAsset[] {
    if (!projectId) return [];
    return loadAssets().filter((a) => a.projectId === projectId);
  },
};
